import { useState } from "react";

import dayjs, { type Dayjs } from "dayjs";
import { SlArrowLeft, SlArrowRight } from "react-icons/sl";

import Button from "@/components/Button";
import { daysOfTheWeek } from "@/constants/calendar";
import { getWeeks } from "@/utils/calendar";
import { cn } from "@/utils/cn";

interface CompactDatePickerProps {
  selectedDate: Dayjs;
  onSelectDate: (date: Dayjs) => void;
}

const CompactDatePicker = ({ selectedDate, onSelectDate }: CompactDatePickerProps) => {
  const [firstDayOfMonth, setFirstDayOfMonth] = useState(dayjs().date(1));

  const today = dayjs();

  return (
    <div className="flex w-full flex-col gap-2">
      {/* Month navigation */}
      <div className="flex items-center justify-center gap-5 py-1">
        <Button variant="custom" onClick={() => setFirstDayOfMonth(firstDayOfMonth.subtract(1, "month").date(1))}>
          <SlArrowLeft size={12} />
        </Button>
        <span className="min-w-[130px] text-center text-[15px] font-semibold">
          {firstDayOfMonth.format("YYYY. MM")}
        </span>
        <Button variant="custom" onClick={() => setFirstDayOfMonth(firstDayOfMonth.add(1, "month").date(1))}>
          <SlArrowRight size={12} />
        </Button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 text-center text-[13px] font-medium">
        {daysOfTheWeek.map((day, i) => (
          <div
            key={day}
            className={cn(
              "py-2.5",
              i === 0 && "text-red",
              i === 6 && "text-saturday",
            )}
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      {getWeeks(firstDayOfMonth).map((week) => (
        <div key={week[0].valueOf()} className="grid grid-cols-7 text-center">
          {week.map((day) => {
            const isCurrentMonth = day.isSame(firstDayOfMonth, "month");
            const isSelected = selectedDate && day.isSame(selectedDate, "day");
            const isToday = day.isSame(today, "day");
            const isPast = day.isBefore(today, "day");

            return (
              <div
                key={day.valueOf()}
                className={cn(
                  "flex h-9 items-center justify-center text-[13px] transition-colors",
                  !isCurrentMonth && "opacity-30",
                  isCurrentMonth && !isPast && "cursor-pointer hover:bg-gray-light/50 hover:rounded-lg",
                  isPast && isCurrentMonth && "text-gray-dull",
                  isSelected && "rounded-lg border-2 border-primary bg-primary-light/[.34] font-semibold",
                  !isSelected && "border border-transparent",
                  isToday && !isSelected && "font-bold text-primary",
                  day.day() === 0 && isCurrentMonth && !isPast && "text-red",
                  day.day() === 6 && isCurrentMonth && !isPast && "text-saturday",
                )}
                onClick={() => {
                  if (isCurrentMonth && !isPast) onSelectDate(day);
                }}
              >
                {day.date()}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export default CompactDatePicker;
